/* ==========================================================================
   Minimum order quantity.

   Shopify cannot enforce a minimum below Plus, so this reads the minimum from
   the metafield and holds the quantity box to it: starts at the minimum, will
   not step below it, and snaps back if it is typed under.

   The minimum is PER VARIANT, not per product. On the label ranges it changes
   with the size — 2,000 for a 30mm round against 500 for a 65mm — because it
   is a function of how many labels fit on a roll. So the number in force has
   to be re-read every time the customer changes variant, along with the note
   above the quantity box; a stale note quoting the previous size's minimum is
   worse than no note, because the customer believes it.

   This is a front-of-house guard, not a hard one — a determined person can
   still edit the cart. Every order here is reviewed before it goes to print,
   so that is an acceptable gap; what matters is that the ordinary path cannot
   produce a 1-unit order on a 2,000-unit product by accident.
   ========================================================================== */

(function () {
  'use strict';

  var cfg = window.EUMinQuantity;
  if (!cfg) return;

  var byVariant = cfg.byVariant || {};
  var fallback = cfg.fallback || 0;

  /* Which variant is selected right now. Dawn keeps it in the hidden input the
     product form submits, which is also the thing it rewrites on every variant
     change — so reading it is always current, with no need to hook Dawn's own
     events or know their names. */
  function currentId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    return input ? String(input.value) : null;
  }

  function currentMin() {
    var entry = byVariant[currentId()];
    var min = entry ? entry.min : fallback;
    return min > 1 ? min : 0;
  }

  /* The note lives INSIDE the element the MutationObserver below watches, so
     writing its innerHTML is itself a mutation: paint -> observer -> scan ->
     paint, forever, and the tab locks up. The previous version only ever set
     attributes, which childList/subtree does not report, which is why this did
     not bite until the note started following the variant.

     Remembering what was last painted breaks the cycle: on the second pass the
     html is unchanged, nothing is written, no mutation fires. The observer is
     also parked for the duration as a structural guarantee, so a future edit
     here cannot reintroduce the loop by touching something else. */
  var lastHtml = null;

  function paintNote() {
    var note = document.getElementById('eu-min-note');
    var text = document.getElementById('eu-min-text');
    if (!note) return;
    var entry = byVariant[currentId()];
    var min = currentMin();
    if (!min) { note.hidden = true; return; }
    note.hidden = false;
    if (text && entry && entry.html && entry.html !== lastHtml) {
      lastHtml = entry.html;
      text.innerHTML = entry.html;
    }
  }

  function apply(input) {
    if (!input) return;
    var min = currentMin();

    if (!min) {
      input.removeAttribute('min');
      input.dataset.minApplied = '';
      return;
    }

    input.setAttribute('min', min);
    input.setAttribute('step', cfg.step || 1);

    /* Raise the quantity to the new minimum, but never lower an order somebody
       has deliberately typed up — switching from a 500 size to a 2,000 one has
       to move them up to 2,000, while switching the other way must not quietly
       cut an order of 3,000 down to 500. */
    var n = parseInt(input.value, 10);
    if (isNaN(n) || n < min) input.value = min;

    if (input.dataset.minApplied) return;
    input.dataset.minApplied = '1';
    input.addEventListener('change', clamp);
    input.addEventListener('blur', clamp);

    function clamp() {
      var floor = currentMin();
      if (!floor) return;
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < floor) {
        input.value = floor;
        flash();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function flash() {
    var note = document.getElementById('eu-min-note');
    if (!note) return;
    note.classList.add('eu-min-note--flash');
    setTimeout(function () { note.classList.remove('eu-min-note--flash'); }, 900);
  }

  var watcher = null;

  function scan() {
    if (watcher) watcher.disconnect();
    try {
      paintNote();
      document.querySelectorAll('input[name="quantity"]').forEach(apply);
    } finally {
      /* takeRecords() drops anything this scan caused, so reconnecting does not
         immediately re-deliver our own writes. */
      if (watcher) { watcher.takeRecords(); reconnect(); }
    }
  }

  /* The snippet renders above the quantity block, so at this point the input
     does not exist yet and a single scan finds nothing — which is how this
     first shipped, showing quantity 1 on a 250-minimum product. Scan now for
     the case where it does exist, and again once the document is parsed. */
  scan();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  }
  window.addEventListener('load', scan);

  /* Dawn re-renders both the quantity input and the hidden variant id when a
     variant changes, which drops the listeners and the min attribute with it.
     Watching the section catches that without needing to know Dawn's
     internals. */
  function reconnect() {
    var host = document.querySelector('.product, [id^="MainProduct"], main');
    if (host && watcher) watcher.observe(host, { childList: true, subtree: true });
  }

  function observe() {
    if (!window.MutationObserver || watcher) return;
    watcher = new MutationObserver(scan);
    reconnect();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
  else observe();

  /* A variant change fires a change event on the hidden id input in some Dawn
     versions before the section re-renders, so catch that path too. */
  document.addEventListener('change', function (e) {
    if (!e.target) return;
    if (e.target.name === 'quantity') apply(e.target);
    else if (e.target.name === 'id' || e.target.closest?.('variant-selects, variant-radios')) scan();
  });
})();
